/**
 * One synchronous boundary for all protected work. The dormant default preserves
 * auth-disabled development. Only the authentication owner changes this state.
 */
let generation = 0;
let blocked = false;
let controller = new AbortController();
const listeners = new Set<() => void>();
const retirements = new Set<() => void>();

export function sessionGeneration(): number { return generation; }
export function isSessionBlocked(): boolean { return blocked; }
export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export function registerSessionRetirement(retire: () => void): () => void {
  retirements.add(retire);
  return () => { retirements.delete(retire); };
}

/** Abort first, then retire caches, before notifying any session consumers. */
export function changeSessionBoundary(nextBlocked: boolean): void {
  if (blocked === nextBlocked) return;
  blocked = nextBlocked;
  generation += 1;
  controller.abort();
  controller = new AbortController();
  try {
    for (const retire of retirements) {
      try { retire(); } catch { console.error('A retired session cache could not be cleared.'); }
    }
  } finally {
    for (const listener of listeners) listener();
  }
}

export function captureSession(signal?: AbortSignal | null) {
  const origin = generation;
  const boundarySignal = controller.signal;
  const combined = signal ? AbortSignal.any([boundarySignal, signal]) : boundarySignal;
  const assertCurrent = () => {
    combined.throwIfAborted();
    if (blocked || generation !== origin) throw new DOMException('Session ended.', 'AbortError');
  };
  assertCurrent();
  return { signal: combined, assertCurrent };
}
